// LIBRERIE/IMPORTS
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Select, { MultiValue, OnChangeValue } from 'react-select';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import debounce from 'lodash.debounce';

// CONFIGURAZIONI
import appConfig, { AGENCY_ID } from '../../../config/appConfig';
import { contractTranslations, getContractTranslation, LanguageCode } from "../../../config/i18n/elementsMappingProperty";
import { getTipologiaTranslation } from "../../../config/i18n/propertyTranslations";

// HOOKS
import { useGetProperties } from '../../../hooks/useGetProperties';
import { useGetAddresses } from '../../../hooks/useGetAddresses';

interface Option {
  value: number | string | null;
  label: string | null;
}

interface FilterState {
  contract: Option | null;
  tipologia: Option[];
  provincia: Option | null;
  comune: Option[];
  frazione: Option[];
  priceMin: number;
  priceMax: number;
  ref: string;
  sqmtMin: number;
  sqmtMax: number;
  roomMax: number;
  bathroomMax: number;
  gardenPrivate: boolean;
  terrace: boolean;
  swimmingPool: boolean;
}

const defaultFilterState: FilterState = {
  contract: null,
  tipologia: [],
  provincia: null,
  comune: [],
  frazione: [],
  priceMin: 0,
  priceMax: 0,
  ref: "",
  sqmtMin: 0,
  sqmtMax: 0,
  roomMax: 0,
  bathroomMax: 0,
  gardenPrivate: false,
  terrace: false,
  swimmingPool: false,
};

const SearchWidget: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Stati per i filtri
  const [filters, setFilters] = useState<FilterState>(defaultFilterState);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(defaultFilterState);
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper per convertire stringa in numero
  const parseNumber = (value: string | null, fallback = 0): number => {
    const num = Number(value);
    return isNaN(num) ? fallback : num;
  };

  const { data: addresses, loading: loadingAddresses } = useGetAddresses(AGENCY_ID);

  const CAPOLUOGO_VALUE = "30000";

  const isCapoluogoSelected = useCallback((): boolean => {
    return filters.frazione.some(frazione => frazione.value === CAPOLUOGO_VALUE);
  }, [filters.frazione]);


  // Formattazione dei filtri per la query delle proprietà
  // Questa è necessaria per la query delle tipologie e per l'URL
  const formattedFilters = useMemo(() => ({
    contract: appliedFilters.contract ? [Number(appliedFilters.contract.value)] : [],
    type: appliedFilters.tipologia.map(t => Number(t.value)).filter(id => !isNaN(id)),
    provincia: appliedFilters.provincia ? String(appliedFilters.provincia.value) : "",
    comune: appliedFilters.comune.map(cm => String(cm.value)).filter(val => val !== ''),
    frazione: isCapoluogoSelected() ? [CAPOLUOGO_VALUE] : appliedFilters.frazione.map(f => String(f.value)).filter(val => val !== ''),
    maxRooms: appliedFilters.roomMax,
    maxBathrooms: appliedFilters.bathroomMax,
    minPrice: appliedFilters.priceMin,
    maxPrice: appliedFilters.priceMax,
    sqmtMin: appliedFilters.sqmtMin,
    sqmtMax: appliedFilters.sqmtMax,
    ref: appliedFilters.ref || "",
    gardenPrivate: appliedFilters.gardenPrivate,
    terrace: appliedFilters.terrace,
    swimmingPool: appliedFilters.swimmingPool,
  }), [appliedFilters, isCapoluogoSelected, CAPOLUOGO_VALUE]);

  // Per il multiselect di tipologia, eseguo una query senza il filtro tipologia (type vuoto)
  // Questa fetch serve a capire quali tipologie sono disponibili DATO GLI ALTRI FILTRI APPLICATI
  const tipologieFiltersBase = useMemo(() => ({
    contract: formattedFilters.contract,
    type: [], // Filtro vuoto per ottenere tutte le tipologie disponibili con gli altri filtri
    provincia: formattedFilters.provincia,
    comune: formattedFilters.comune,
    frazione: formattedFilters.frazione,
    maxRooms: formattedFilters.maxRooms,
    maxBathrooms: formattedFilters.maxBathrooms,
    minPrice: formattedFilters.minPrice,
    maxPrice: formattedFilters.maxPrice,
    sqmtMin: formattedFilters.sqmtMin,
    sqmtMax: formattedFilters.sqmtMax,
    ref: formattedFilters.ref,
    gardenPrivate: formattedFilters.gardenPrivate,
    terrace: formattedFilters.terrace,
    swimmingPool: formattedFilters.swimmingPool,
  }), [formattedFilters]);


  const { properties: propertiesForTipologie, loading: loadingTipologie } = useGetProperties(AGENCY_ID, {
    limit: 100000000,
    offset: 0,
    withCount: true,
    filters: tipologieFiltersBase
  }, isInitialized);

  // Uso useMemo per calcolare le opzioni di tipologia tradotte basate sui dati fetchedForTipologie
  const tipologieOptions: Option[] = useMemo(() => {
    const uniqueTipologyIds = new Set<number>();
    propertiesForTipologie.forEach(property => {
      const tipologiaValue = property.tipologyDetails?.id;
      if (tipologiaValue && typeof tipologiaValue === 'number') {
        uniqueTipologyIds.add(tipologiaValue);
      }
    });

    // Mappa gli ID univoci alle opzioni tradotte
    const optionsArray = Array.from(uniqueTipologyIds)
      .map(id => ({
        value: id,
        // Ottiene la traduzione per ogni ID nella lingua corrente
        label: getTipologiaTranslation(id, i18n.language as LanguageCode)
      }))
      // Ordina per la label tradotta
      .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

    // Aggiunge l'opzione "(qualsiasi)" all'inizio se ci sono opzioni disponibili (oltre 0)
    if (optionsArray.length > 0) {
      return [{ value: null, label: t('common.(any)') ?? '(qualsiasi)' }, ...optionsArray];
    }

    // Se non ci sono tipologie fetched, mostra solo "(qualsiasi)"
    return [{ value: null, label: t('common.(any)') ?? '(qualsiasi)' }];

  }, [propertiesForTipologie, i18n.language, t]); // Dipende da propertiesForTipologie, lingua e t

  // Inizializzo i filtri dai parametri URL (quando gli indirizzi e le traduzioni sono pronti)
  // Aggiunto i18n.language alle dipendenze per reinizializzare al cambio lingua
  useEffect(() => {
    // Dipendiamo da addresses per i dati geografici e dalla lingua per le traduzioni
    if (addresses && i18n.language) {
      const initFilters: FilterState = {

        // Inizializza Contratto usando la funzione di traduzione
        contract: searchParams.get('contratto')
          ? {
            value: Number(searchParams.get('contratto')),
            label: getContractTranslation(Number(searchParams.get('contratto')), i18n.language as LanguageCode)
          }
          : null,

        // Inizializza Tipologia usando la funzione di traduzione
        tipologia: searchParams.getAll('tipologia').length > 0 ? searchParams.getAll('tipologia').map(tp => {
          const tipologiaId = Number(tp);
          return {
            value: tipologiaId,
            label: getTipologiaTranslation(tipologiaId, i18n.language as LanguageCode)
          };
        }).filter(opt => !isNaN(Number(opt.value))) // Filtra eventuali valori non numerici
          : [],

        // Province, Comuni, Frazioni restano basate sui dati API (non tradotte da elementsMapping)
        provincia: searchParams.get('provincia')
          ? {
            value: searchParams.get('provincia'),
            label: addresses.province.find(prov => prov.codiceIstat === searchParams.get('provincia'))?.nome ||
              searchParams.get('provincia')
          }
          : null,

        comune: searchParams.getAll('comune').map(cm => {
          // Trovo il comune corrispondente nell'array di comuni
          const comune = addresses.comuni.find(c => c.codiceIstatComuneAlfa === cm);
          return {
            value: cm, // Uso il codice ISTAT come valore
            label: comune ? comune.nomeIt ?? cm : cm, // Uso il nome del comune se lo trovo, altrimenti uso il codice ISTAT
          };
        }).filter(opt => opt.value !== ''), // Filtra eventuali valori vuoti

        frazione: searchParams.getAll('frazione').map(f => ({
          value: f,
          // Per CAPOLUOGO usiamo la traduzione i18n, altrimenti il nome dall'API
          label: f === CAPOLUOGO_VALUE ? t('searchWidget.capoluogoLabel') : addresses.frazioni.find(fraz => String(fraz.id) === f || fraz.frazione === f)?.frazione || f,
        })).filter(opt => opt.value !== null && opt.value !== ''), // Filtra eventuali valori null/vuoti


        priceMin: parseNumber(searchParams.get('prezzo_min')),
        priceMax: parseNumber(searchParams.get('prezzo_max')),
        ref: searchParams.get('riferimento') || "",
        sqmtMin: parseNumber(searchParams.get('mq_min')),
        sqmtMax: parseNumber(searchParams.get('mq_max')),
        roomMax: parseNumber(searchParams.get('camere_max')),
        bathroomMax: parseNumber(searchParams.get('bagni_max')),
        gardenPrivate: searchParams.get('giardino_privato') === 'true',
        terrace: searchParams.get('terrazzo') === 'true',
        swimmingPool: searchParams.get('piscina') === 'true',
      };

      // Applica i filtri letti dall'URL
      setFilters(initFilters);
      setAppliedFilters(initFilters); // Aggiorno appliedFilters immediatamente
      setIsInitialized(true); // Imposto isInitialized a true
    }
    // Dipendenze: addresses (dati API), searchParams (cambio URL), i18n.language (cambio lingua)
  }, [addresses, searchParams, i18n.language, t]); // Aggiunto t come dipendenza

  // Funzione per aggiornare l'URL (debounced)
  const updateURL = useCallback(
    debounce((newFilters: FilterState) => {
      const params = new URLSearchParams();
      if (newFilters.contract?.value) params.set('contratto', String(newFilters.contract.value));
      newFilters.tipologia.forEach(tp => {
        if (tp.value !== null && tp.value !== '') params.append('tipologia', String(tp.value));
      });
      if (newFilters.provincia?.value) params.set('provincia', String(newFilters.provincia.value));
      newFilters.comune.forEach(cm => {
        if (cm.value !== null && cm.value !== '') params.append('comune', String(cm.value));
      });

      // Gestione Frazioni (CAPOLUOGO)
      if (isCapoluogoSelected()) {
        params.set('frazione', CAPOLUOGO_VALUE);
      } else {
        newFilters.frazione.forEach(f => {
          if (f.value !== null && f.value !== '') params.append('frazione', String(f.value));
        });
      }

      if (newFilters.roomMax > 0) params.set('camere_max', String(newFilters.roomMax));
      if (newFilters.bathroomMax > 0) params.set('bagni_max', String(newFilters.bathroomMax));
      if (newFilters.sqmtMin > 0) params.set('mq_min', String(newFilters.sqmtMin));
      if (newFilters.sqmtMax > 0) params.set('mq_max', String(newFilters.sqmtMax));
      if (newFilters.priceMin > 0) params.set('prezzo_min', String(newFilters.priceMin));
      if (newFilters.priceMax > 0) params.set('prezzo_max', String(newFilters.priceMax));
      if (newFilters.ref) params.set('riferimento', newFilters.ref);

      // Gestione dei Checkbox (Booleans) - Aggiunge solo se true
      if (newFilters.gardenPrivate) params.set('giardino_privato', 'true');
      if (newFilters.terrace) params.set('terrazzo', 'true');
      if (newFilters.swimmingPool) params.set('piscina', 'true');

      if (appConfig.integrations.multilang) {
        const currentPathWithoutLang = window.location.pathname.replace(`/${i18n.language}`, '');
        const newPath = `/${i18n.language}${currentPathWithoutLang}`;
        const queryString = params.toString();
        const targetUrlWithLang = `${newPath}${queryString ? '?' + queryString : ''}`;
        navigate(targetUrlWithLang, { replace: true });
      } else {
        const queryString = params.toString();
        const targetUrl = `${queryString ? '?' + queryString : ''}`;
        navigate(targetUrl, { replace: true });
      }
    }, 500),
    [navigate, isCapoluogoSelected, CAPOLUOGO_VALUE, i18n.language]
  );

  const handleSearch = () => {
    setAppliedFilters(filters);
    updateURL(filters);
  };

  // --- Generazione delle opzioni per i Select ---

  // Build options for "contratto" (derivate dalle traduzioni importate)
  const contratti: Option[] = useMemo(() => {
    const options = contractTranslations.map((contratto) => ({
      value: contratto.id,
      // Usa la funzione di traduzione per la label
      label: getContractTranslation(contratto.id, i18n.language as LanguageCode),
    }))
      // Ordina per label tradotta
      .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

    // Aggiungi l'opzione "(qualsiasi)" solo se ci sono contratti disponibili
    if (options.length > 0) {
      return [{ value: null, label: t('common.(any)') ?? '(qualsiasi)' }, ...options];
    }
    return [{ value: null, label: t('common.(any)') ?? '(qualsiasi)' }];

  }, [i18n.language, t]);

  // Province options (non tradotte da elementsMapping)
  const provinceOptions: Option[] = useMemo(() => {
    const options = addresses?.province.map((provincia) => ({
      value: provincia.codiceIstat ?? null,
      label: provincia.nome ?? null,
    })).filter(opt => opt.value !== null) ?? [];

    return [{ value: null, label: t('common.(any)') ?? '(qualsiasi)' }, ...options.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))];

  }, [addresses?.province, t]);

  // Comuni options (filtrati per provincia selezionata, non tradotti da elementsMapping)
  const comuniOptions: Option[] = useMemo(() => {
    const options = addresses?.comuni
      .filter((comune) => comune.codiceIstatProvincia === filters.provincia?.value)
      .map((comune) => ({
        value: comune.codiceIstatComuneAlfa ?? null,
        label: comune.nomeIt ?? null,
      })).filter(opt => opt.value !== null) ?? []; // Filtra eventuali comuni senza codice

    return [{ value: null, label: t('common.(any)') ?? '(qualsiasi)' }, ...options.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))];

  }, [addresses?.comuni, filters.provincia, t]);


  // Frazioni options (filtrate per comune/i selezionato/i, "Capoluogo" tradotto via i18n)
  const frazioniOptions: Option[] = useMemo(() => {
    const frazioniBase: Option[] = (filters.comune.length > 0 && addresses?.frazioni
      ? addresses.frazioni
        .filter((frazione) => filters.comune.some(c => c.value === frazione.comuneIstat))
        .map((frazione) => ({
          value: frazione.id?.toString() ?? null,
          label: frazione.frazione ?? null,
        }))
        .filter(opt => opt.value !== null)
        .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))
      : []) ?? [];

    const showCapoluogoOption = filters.comune.length > 0;

    const options: Option[] = [
      ...(showCapoluogoOption ? [{ value: CAPOLUOGO_VALUE, label: t('searchWidget.capoluogoLabel') ?? 'CAPOLUOGO' }] : []),
      ...frazioniBase,
    ];

    return [{ value: null, label: t('common.(any)') ?? '(qualsiasi)' }, ...options];

  }, [addresses?.frazioni, filters.comune, CAPOLUOGO_VALUE, t]);


  const preventScrollOnFocus = (e: React.FocusEvent<HTMLInputElement> | React.FocusEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // --- Handlers per Select e Input ---
  const handleGenericChange = (field: keyof FilterState) => (option: OnChangeValue<Option, false>) => {
    // Per select a singolo valore
    setFilters(prev => ({ ...prev, [field]: option }));

    // Reset comuni e frazioni se cambia provincia
    if (field === 'provincia') {
      setFilters(prev => ({ ...prev, comune: [], frazione: [] }));
    }
  };

  const handleMultiChange = (field: keyof FilterState) => (options: MultiValue<Option>) => {
    const validOptions = (Array.isArray(options) ? options : []).filter(opt => opt.value !== null);
    setFilters(prev => ({ ...prev, [field]: validOptions }));

    if (field === 'comune') {
      setFilters(prev => ({ ...prev, frazione: [] }));
    }
  };

  const handleNumberChange = (field: keyof FilterState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, [field]: value === "" ? "" : parseInt(value, 10) || 0 }));
  };

  // Handler specifico per i checkbox (aggiunto)
  const handleCheckboxChange = (field: keyof FilterState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, [field]: e.target.checked }));
  };


  const handleTextChange = (field: keyof FilterState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleReset = () => {
    const initialFiltersFromUrl: FilterState = {
      contract: searchParams.get('contratto')
        ? { value: Number(searchParams.get('contratto')), label: getContractTranslation(Number(searchParams.get('contratto')), i18n.language as LanguageCode) }
        : null,

      tipologia: searchParams.getAll('tipologia').length > 0 ? searchParams.getAll('tipologia').map(tp => {
        const tipologiaId = Number(tp);
        return {
          value: tipologiaId,
          label: getTipologiaTranslation(tipologiaId, i18n.language as LanguageCode)
        };
      }).filter(opt => !isNaN(Number(opt.value)))
        : [],

      provincia: searchParams.get('provincia')
        ? {
          value: searchParams.get('provincia'),
          label: addresses?.province.find(prov => prov.codiceIstat === searchParams.get('provincia'))?.nome ||
            searchParams.get('provincia')
        }
        : null,

      comune: searchParams.getAll('comune').map(cm => {
        const comune = addresses?.comuni.find(c => c.codiceIstatComuneAlfa === cm);
        return {
          value: cm,
          label: comune ? comune.nomeIt ?? cm : cm,
        };
      }).filter(opt => opt.value !== null && opt.value !== ''),

      frazione: searchParams.getAll('frazione').map(f => ({
        value: f,
        label: f === CAPOLUOGO_VALUE ? t('searchWidget.capoluogoLabel') : addresses?.frazioni.find(fraz => String(fraz.id) === f || fraz.frazione === f)?.frazione || f,
      })).filter(opt => opt.value !== null && opt.value !== ''),


      priceMin: parseNumber(searchParams.get('prezzo_min')),
      priceMax: parseNumber(searchParams.get('prezzo_max')),
      ref: searchParams.get('riferimento') || "",
      sqmtMin: parseNumber(searchParams.get('mq_min')),
      sqmtMax: parseNumber(searchParams.get('mq_max')),
      roomMax: parseNumber(searchParams.get('camere_max')),
      bathroomMax: parseNumber(searchParams.get('bagni_max')),
      gardenPrivate: searchParams.get('giardino') === 'true',
      terrace: searchParams.get('terrazzo') === 'true',
      swimmingPool: searchParams.get('piscina') === 'true',
    };
    const currentUrlParams = new URLSearchParams(window.location.search);
    if (currentUrlParams.toString() === '') {
      setFilters(defaultFilterState);
      setAppliedFilters(defaultFilterState);
    } else {
      setFilters(initialFiltersFromUrl);
      setAppliedFilters(initialFiltersFromUrl);
    }

    if (appConfig.integrations.multilang) {
      const currentPathWithoutLang = window.location.pathname.replace(`/${i18n.language}`, '');
      const newPath = `/${i18n.language}${currentPathWithoutLang}`;
      const queryString = currentUrlParams.toString();
      const targetUrlWithLang = `${newPath}${queryString ? '?' + queryString : ''}`;
      navigate(targetUrlWithLang, { replace: true });
    } else {
      const queryString = currentUrlParams.toString();
      const targetUrl = `${queryString ? '?' + queryString : ''}`;
      navigate(targetUrl, { replace: true });
    }
  };

  const handleCollapseToggle = () => setIsCollapsed(!isCollapsed);

  // --- Variabili per Traduzioni Comuni ---
  const widgetTitle = t('searchWidget.title');
  const resetTitle = t('searchWidget.resetTitle');
  const openSearchTitle = t('searchWidget.open');
  const closeSearchTitle = t('searchWidget.close');
  const labelContract = t('searchWidget.contract');
  const labelType = t('searchWidget.type');
  const labelProvince = t('searchWidget.province');
  const labelCity = t('searchWidget.city');
  const labelFraction = t('searchWidget.fraction');
  const labelRooms = t('common.rooms');
  const labelBathrooms = t('common.bathrooms');
  const labelSqmtMin = t('property.sqmtMin');
  const labelSqmtMax = t('property.sqmtMax');
  const labelPriceMin = t('property.priceMin');
  const labelPriceMax = t('property.priceMax');
  const labelReference = t('property.referenceExtended');
  const placeholderContract = t('searchWidget.selectContract');
  const placeholderProvince = t('searchWidget.selectProvince');
  const placeholderType = t('searchWidget.type').toUpperCase();
  const placeholderCity = t('searchWidget.city').toUpperCase();
  const placeholderFraction = t('searchWidget.fraction').toUpperCase();
  const placeholderRoomsMax = t('searchWidget.fieldMax', { field: labelRooms });
  const placeholderBathroomsMax = t('searchWidget.fieldMax', { field: labelBathrooms });
  const labelGarden = t('property.garden.label');
  const labelTerrace = t('property.terrace');
  const labelPool = t('property.pool');
  const filterButtonText = t('searchWidget.filterResults');
  const msgNoResults = t('common.noResults', 'Nessun risultato');
  const msgSelectProvinceFirst = t('searchWidget.selectProvinceFirst', 'Seleziona prima una provincia');
  const msgSelectOneCityFirst = t('searchWidget.selectOneCityFirst', 'Seleziona un solo comune');

  const isLoadingAnything = loadingAddresses || loadingTipologie || !isInitialized;

  function backToTop() {
    window.scrollTo(0, 0);
  }
  // Al submit del'form manda le 2 funzioni
  function handleSearchAndBackToTop() {
    handleSearch();
    backToTop();
  }

  return (
    <div className="widget">
      <div className="widget-title widget-collapse">
        <h6 className="mb-0">{widgetTitle}</h6>
        {/* Bottone Reset */}
        <button type="button" id="no-brd-btn" className="ml-auto text-danger" title={resetTitle ?? ''} onClick={handleReset} disabled={isLoadingAnything}>
          <i className="fas fa-ban" aria-hidden="true"></i>
          <span className="sr-only">{resetTitle}</span>
        </button>
        {/* Bottone Collapse */}
        <button
          id="no-brd-btn"
          className="text-secondary"
          title={isCollapsed ? openSearchTitle ?? '' : closeSearchTitle ?? ''}
          onClick={handleCollapseToggle}
          aria-expanded={!isCollapsed}
          aria-controls="filter-property-content"
        >
          <i className={`fas fa-chevron-${isCollapsed ? "down" : "up"}`} aria-hidden="true" />
          <span className="sr-only">{isCollapsed ? openSearchTitle : closeSearchTitle}</span>
        </button>
      </div>
      <div className={`collapse ${isCollapsed ? "" : "show"}`} id="filter-property">
        <form
          action="immobili" // L'action non è strettamente necessaria dato l'handleSubmit con navigate
          method="get" // Il method non è strettamente necessario
          id="search"
          acceptCharset="utf-8"
          className="form-row basic-select-wrapper"
          onSubmit={(e) => e.preventDefault()} // Previene il submit HTML nativo
        >
          {/* Contratto - ORA TRADOTTO */}
          <div id="select-widget" className="input-group mb-2 select-border">
            <label className="form-label small" htmlFor="select_contract">{labelContract}</label>
            <Select<Option, false> // Specifica i tipi per Option
              inputId="select_contract"
              name="contratto"
              options={contratti} // Usa le opzioni generate con le traduzioni
              // Trova l'opzione corretta in base al valore nello stato filters
              value={contratti.find((c) => c.value === filters.contract?.value) || null}
              onChange={handleGenericChange('contract')}
              placeholder={placeholderContract}
              className="basic-select"
              classNamePrefix="react-select"
              onFocus={preventScrollOnFocus}
              isClearable
              menuPortalTarget={document.body}
              styles={{
                container: (provided) => ({ ...provided, width: "100%" }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              aria-label={labelContract ?? ''}
            />
          </div>

          {/* Tipologia (MultiSelect) - ORA TRADOTTO e filtrato per quelle dell'API */}
          <div id="select-widget" className="input-group mb-2 select-border">
            <label className="form-label small" htmlFor="select_tipologia">{labelType}</label>
            <Select<Option, true> // Specifica i tipi per Option e isMulti = true
              inputId='select_tipologia'
              name="tipologia"
              isMulti
              options={tipologieOptions} // Usa le opzioni generate con le traduzioni E filtrate
              value={filters.tipologia}
              onChange={handleMultiChange('tipologia')}
              onFocus={preventScrollOnFocus}
              placeholder={placeholderType}
              closeMenuOnSelect={false}
              className="basic-select"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={{
                container: (provided) => ({ ...provided, width: "100%" }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              aria-label={labelType ?? ''}
            />
          </div>

          {/* Provincia - NON TRADOTTO con elementsMappingProperty.ts */}
          <div id="select-widget" className="input-group mb-2 select-border">
            <label className="form-label small" htmlFor="select_provincia">{labelProvince}</label>
            <Select<Option, false> // Specifica i tipi per Option
              inputId='select_provincia'
              options={provinceOptions} // Usa le opzioni basate sui dati API
              value={filters.provincia}
              onChange={handleGenericChange('provincia')}
              placeholder={placeholderProvince}
              isClearable
              className="basic-select"
              classNamePrefix="react-select"
              onFocus={preventScrollOnFocus}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                container: (provided) => ({ ...provided, width: "100%" })
              }}
              aria-label={labelProvince ?? ''}
            />
          </div>

          {/* Comune - NON TRADOTTO con elementsMappingProperty.ts */}
          <div id="select-widget" className="input-group mb-2 select-border">
            <label className="form-label small" htmlFor="select_comune">{labelCity}</label>
            <Select<Option, true> // Specifica i tipi per Option e isMulti = true
              inputId='select_comune'
              name="comune"
              isMulti
              options={comuniOptions} // Usa le opzioni basate sui dati API e filtrate per provincia
              value={filters.comune}
              onChange={handleMultiChange('comune')}
              isDisabled={!filters.provincia} // Disabilita se provincia non selezionata
              placeholder={placeholderCity}
              closeMenuOnSelect={false}
              className="basic-select"
              classNamePrefix="react-select"
              onFocus={preventScrollOnFocus}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                container: (provided) => ({ ...provided, width: "100%" })
              }}
              aria-label={labelCity ?? ''}
              noOptionsMessage={() => filters.provincia ? msgNoResults : msgSelectProvinceFirst} // Messaggi per l'utente
            />
          </div>

          {/* Frazione (MultiSelect) - "Capoluogo" tradotto via i18n */}
          <div id="select-widget" className="input-group mb-2 select-border">
            <label className="form-label small" htmlFor="select_frazione">{labelFraction}</label>
            <Select<Option, true> // Specifica i tipi per Option e isMulti = true
              inputId='select_frazione'
              name="frazione"
              options={frazioniOptions} // Usa le opzioni basate sui dati API e filtrate per comune
              value={filters.frazione}
              onChange={handleMultiChange('frazione')}
              // Disabilita se non c'è una provincia O se sono selezionati più di un comune
              isDisabled={!filters.provincia || filters.comune.length === 0 || filters.comune.length > 1}
              placeholder={placeholderFraction}
              closeMenuOnSelect={false}
              onFocus={preventScrollOnFocus}
              isMulti
              className="basic-select"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                container: (provided) => ({ ...provided, width: "100%" })
              }}
              aria-label={labelFraction ?? ''}
              noOptionsMessage={() => !filters.provincia || filters.comune.length === 0 || filters.comune.length > 1 ? msgSelectOneCityFirst : msgNoResults} // Messaggi per l'utente
            />
          </div>

          {/* Numeric and text inputs */}
          <div className="input-group mb-2">
            <label className="form-label small" htmlFor="input_camere_max">{placeholderRoomsMax}</label>
            <input
              type="number"
              id="input_camere_max"
              name="camere_max"
              className="form-control form-control-sm"
              min="0"
              step="1"
              value={filters.roomMax > 0 ? filters.roomMax : ""} // Mostra stringa vuota se 0 per un placeholder più pulito
              onChange={handleNumberChange("roomMax")}
              placeholder={labelRooms}
              aria-label={placeholderRoomsMax ?? ''}
            />
          </div>

          <div className="input-group mb-2">
            <label className="form-label small" htmlFor="input_bagni_max">{placeholderBathroomsMax}</label>
            <input
              type="number"
              id="input_bagni_max"
              name="bagni_max"
              className="form-control form-control-sm"
              min="0"
              step="1"
              value={filters.bathroomMax > 0 ? filters.bathroomMax : ""} // Mostra stringa vuota se 0
              onChange={handleNumberChange("bathroomMax")}
              placeholder={labelBathrooms}
              aria-label={placeholderRoomsMax ?? ''}
            />
          </div>

          <div className="input-group mb-2">
            <label className="form-label small" htmlFor="input_mq_min">{labelSqmtMin}</label>
            <input
              type="number"
              id="input_mq_min"
              name="mq_min"
              className="form-control form-control-sm"
              min="0"
              step="10"
              value={filters.sqmtMin > 0 ? filters.sqmtMin : ""} // Mostra stringa vuota se 0
              onChange={handleNumberChange("sqmtMin")}
              placeholder={labelSqmtMin}
              aria-label={labelSqmtMin ?? ''}
            />
          </div>

          <div className="input-group mb-2">
            <label className="form-label small" htmlFor="input_mq_max">{labelSqmtMax}</label>
            <input
              type="number"
              id="input_mq_max"
              name="mq_max"
              className="form-control form-control-sm"
              min="0"
              step="10"
              value={filters.sqmtMax > 0 ? filters.sqmtMax : ""} // Mostra stringa vuota se 0
              onChange={handleNumberChange("sqmtMax")}
              placeholder={labelSqmtMax}
              aria-label={labelSqmtMax ?? ''}
            />
          </div>

          <div className="input-group mb-2">
            <label className="form-label small" htmlFor="input_prezzo_min">{labelPriceMin}</label>
            <input
              type="number"
              id="input_prezzo_min"
              name="prezzo_min"
              className="form-control form-control-sm"
              min="0"
              step="1000" // Step ridotto per maggiore usabilità
              value={filters.priceMin > 0 ? filters.priceMin : ""} // Mostra stringa vuota se 0
              onChange={handleNumberChange("priceMin")}
              placeholder={labelPriceMin}
              aria-label={labelPriceMin ?? ''}
            />
          </div>

          <div className="input-group mb-2">
            <label className="form-label small" htmlFor="input_prezzo_max">{labelPriceMax}</label>
            <input
              type="number"
              id="input_prezzo_max"
              name="prezzo_max"
              className="form-control form-control-sm"
              min="0"
              step="1000" // Step ridotto per maggiore usabilità
              value={filters.priceMax > 0 ? filters.priceMax : ""} // Mostra stringa vuota se 0
              onChange={handleNumberChange("priceMax")}
              placeholder={labelPriceMax}
              aria-label={labelPriceMax ?? ''}
            />
          </div>

          <div className="input-group mb-2">
            <label className="form-label small" htmlFor="input_ref">{labelReference}</label>
            <input
              type="text"
              id="input_ref"
              name="ref"
              className="form-control form-control-sm"
              value={filters.ref}
              onChange={handleTextChange('ref')}
              placeholder={labelReference}
              aria-label={labelReference ?? ''}
            />
          </div>

          {/* Checkbox per Giardino, Terrazzo, Piscina */}
          <div className="input-group mb-2 checkbox-group">
            <label className="form-label small">Altri filtri</label>
            <div className="d-flex flex-wrap">
              {/* Giardino */}
              <div className="form-check custom-control custom-checkbox mr-3 mb-2">
                <input
                  type="checkbox"
                  id="giardino"
                  name="giardino"
                  className="custom-control-input"
                  checked={filters.gardenPrivate}
                  onChange={handleCheckboxChange('gardenPrivate')}
                  aria-label={labelGarden ?? ''}
                />
                <label className="custom-control-label" htmlFor="giardino">{labelGarden}</label>
              </div>
              {/* Terrazzo */}
              <div className="form-check custom-control custom-checkbox mr-3 mb-2">
                <input
                  type="checkbox"
                  id="terrazzo"
                  name="terrazzo"
                  className="custom-control-input"
                  checked={filters.terrace}
                  onChange={handleCheckboxChange('terrace')}
                  aria-label={labelTerrace ?? ''}
                />
                <label className="custom-control-label" htmlFor="terrazzo">{labelTerrace}</label>
              </div>
              {/* Piscina */}
              <div className="form-check custom-control custom-checkbox mb-2">
                <input
                  type="checkbox"
                  id="piscina"
                  name="piscina"
                  className="custom-control-input"
                  checked={filters.swimmingPool}
                  onChange={handleCheckboxChange('swimmingPool')}
                  aria-label={labelPool ?? ''}
                />
                <label className="custom-control-label" htmlFor="piscina">{labelPool}</label>
              </div>
            </div>
          </div>


          {/* Bottone Cerca */}
          <div className="input-group mb-2">
            <button
              className="btn btn-primary btn-block align-items-center text-uppercase"
              type="button" // Usa type="button" per evitare submit nativo e usare l'onClick custom
              onClick={handleSearchAndBackToTop}
              disabled={isLoadingAnything}
            >
              <i className='fas fa-filter mr-1'></i>
              <span>{filterButtonText}</span>
            </button>
          </div>
          {/* Input nascosti mantenuti per compatibilità form, ma non usati dallo script */}
          <input type="hidden" id="input_order_by" name="order_by" value="id" />
          <input type="hidden" id="input_order_dir" name="order_dir" value="desc" />
        </form>
      </div>
    </div>
  );
};

export default SearchWidget;